// @ts-strict-ignore
import { Columns } from "@fullscript/aviary-web";
import type { SyntheticEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Checkbox, Link, Typography, useToast, Arrange, Spacer } from "@aviary";
import {
  isLengthValid,
  isEmailAtValid,
} from "@shared/components/EmailInput/EmailValidation/EmailValidation";
import { useFlippers } from "@shared/context/FlippersContext";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { useLocation, useNavigate } from "@shared/react-router-dom/react-router-dom";
import type { SignInAdditionalAttributesType } from "@shared/types/SignInAdditionalAttributesType";
import { StorePlatform } from "@shared/types/StorePlatform";
import type {
  AuthUserSignInError,
  EmersonAuthUserSignInError,
  TwoFactorAuthenticationMethods,
} from "@shared/types/graphqlGenerated";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { RecaptchaErrorBox } from "@unauthenticated/shared/components/RecaptchaErrorBox/RecaptchaErrorBox";
import { UnauthorizedErrorBox } from "@unauthenticated/shared/components/UnauthorizedErrorBox/UnauthorizedErrorBox";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { useEmersonSignInUnauthenticatedSharedMutation as useEmersonUserSignIn } from "@unauthenticated/shared/data/mutations/EmersonUserSignIn.mutation.generated";
import {
  useUserSignInUnauthenticatedSharedMutation as useUserSignIn,
  type UserSignInUnauthenticatedSharedMutation as UserSignInMutationData,
} from "@unauthenticated/shared/data/mutations/UserSignIn.mutation.generated";
import { e2eAttributes } from "@unauthenticated/shared/e2eAttributes";
import { l } from "@unauthenticated/shared/locales/i18n";

import { AuthenticationLockedMessage } from "../AuthenticationLockedMessage/AuthenticationLockedMessage";

import { SignInEmailInput } from "./SignInEmailInput";
import { SignInPasswordInput } from "./SignInPasswordInput";

import * as styles from "./SignInForm.styles";

interface TwoFactorData {
  otpMethod: TwoFactorAuthenticationMethods;
  userEmail: string;
  emailMasked: string;
  phoneLast4: string;
  authToken: string;
}

interface Props {
  additionalAttributes?: SignInAdditionalAttributesType;
  setHasInputErrors?: (value: boolean) => void;
  forgotPasswordLink: string;
  onCompleted?: (completedData: UserSignInMutationData) => void;
  isOAuth?: boolean;
  defaultEmail?: string;
  onShowTwoFactorFlow?: (data: TwoFactorData) => void;
  isIntake?: boolean;
  onBackButtonClick?: () => void;
}

const SignInForm = ({
  additionalAttributes = {},
  setHasInputErrors,
  forgotPasswordLink,
  onCompleted,
  isOAuth,
  defaultEmail,
  onShowTwoFactorFlow,
  isIntake,
  onBackButtonClick,
}: Props) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const urlEmail = new URLSearchParams(search).get("email");
  const captchaBypassToken = new URLSearchParams(search).get("captchaBypassToken");

  const { t } = useTranslation();
  const { storePlatform } = useSharedGlobalConfig();
  const [isMandatoryPracMFAEnabled] = useFlippers("iam_mandatory_prac_mfa");

  const [email, setEmail] = useState(urlEmail || defaultEmail || "");
  const [password, setPassword] = useState("");
  const [isEmailDirty, setIsEmailDirty] = useState(false);
  const [isPasswordDirty, setIsPasswordDirty] = useState(false);
  const [isCredentialsIncorrect, setIsCredentialsIncorrect] = useState(false);
  const [isUserAuthenticationLocked, setIsUserAuthenticationLocked] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);

  const { makeToast } = useToast();
  const { tablet } = useBreakpoints();

  const isEmailAutoFocus = !urlEmail && !defaultEmail && tablet.greaterThan;
  const isPasswordAutoFocus = !isEmailAutoFocus;

  const handleMutationErrors = (errors: EmersonAuthUserSignInError | AuthUserSignInError) => {
    if (
      errors?.message?.includes("credentials") ||
      errors?.message?.match(/Could not find user/i) ||
      errors?.message?.includes("Emerson Wholesale not enabled")
    ) {
      if (setHasInputErrors) setHasInputErrors(true);
      setIsCredentialsIncorrect(true);
    }

    if (errors?.message?.includes("ERR-41D-E74C")) {
      setIsUserAuthenticationLocked(true);
    }

    if (errors?.message?.match(/Captcha/i)) {
      makeToast("error", t(l.signIn.CaptchaError));
    }

    if (errors?.message?.match(/EmersonSignIn/i)) {
      makeToast("error", t(l.signIn.EmersonCaptchaError));
    }

    setAuthError(errors?.message?.includes("authorized"));
  };

  const handle2FA = (authToken, otpMethod, userEmail, emailMasked, phoneLast4) => {
    if (onShowTwoFactorFlow) {
      onShowTwoFactorFlow({
        otpMethod,
        userEmail,
        emailMasked,
        phoneLast4,
        authToken,
      });
    }
  };

  const fullscriptMutationOptions = {
    onCompleted: (completedData: UserSignInMutationData) => {
      const {
        errors,
        redirectPath,
        twoFactorAuthenticationToken: mfaToken,
        otpMethod,
        userEmail,
        emailMasked,
        phoneLast4,
      } = completedData?.auth?.userSignIn;
      const userHasMfaSetup = !!otpMethod;

      handleMutationErrors(errors);

      // mfaToken would only be present in two cases: Mandatory MFA setup required or MFA already enabled
      if (mfaToken) {
        if (isMandatoryPracMFAEnabled && !userHasMfaSetup) {
          return navigate(authRoutes.setup_mfa, { state: { mfaToken } });
        }
        handle2FA(mfaToken, otpMethod, userEmail, emailMasked, phoneLast4);
      } else if (redirectPath?.startsWith(authRoutes.setup_mfa)) {
        return navigate(authRoutes.setup_mfa, {
          state: { redirectPath },
        });
      }

      // Trigger email verification if account is marked for it
      if (errors?.message?.match(/\(code-101\)/i)) {
        const emailToDisplay = userEmail || email;
        navigate(authRoutes.verify_email, { state: { email: emailToDisplay } });
      }

      if (onCompleted) {
        onCompleted(completedData);
      } else {
        if (redirectPath) {
          window.location.assign(redirectPath);
        }
      }
    },
    onError: ({ message }) => {
      makeToast("error", message);
    },
  };

  const emersonMutationOptions = {
    onCompleted: completedData => {
      const {
        errors,
        redirectPath,
        twoFactorAuthenticationToken: mfaToken,
        otpMethod,
        userEmail,
        emailMasked,
        phoneLast4,
      } = completedData?.auth?.emersonSignIn;

      handleMutationErrors(errors);

      // we aren't enforcing mandatory MFA for Emerson users right now, but if they have it use it
      if (mfaToken) {
        handle2FA(mfaToken, otpMethod, userEmail, emailMasked, phoneLast4);
      }

      if (onCompleted) {
        onCompleted(completedData);
      } else {
        if (redirectPath) {
          window.location.assign(redirectPath);
        }
      }
    },
    onError: ({ message }) => {
      makeToast("error", message);
    },
  };

  const [signInUser, { loading: isSignInLoading }] = useUserSignIn(fullscriptMutationOptions);

  const [signInEmersonUser, { loading: isEmersonSignInLoading }] =
    useEmersonUserSignIn(emersonMutationOptions);

  const signInMutationVariables = (captchaToken: string) => ({
    variables: {
      input: {
        captchaToken,
        attributes: {
          email,
          password,
          rememberMe,
          optional: { ...additionalAttributes, storePlatform },
          intake: isIntake,
        },
      },
    },
  });

  const getPasswordPrompt = () => {
    if (!urlEmail && !defaultEmail) return null;

    return <Typography type="footnote">{t(l.common.PasswordPrompt)}</Typography>;
  };

  const handleEmailOnBlur = () => {
    if (email) {
      setIsEmailDirty(true);
    }
  };

  const handleEmailOnChange = e => {
    setIsCredentialsIncorrect(false);
    setIsUserAuthenticationLocked(false);
    setEmail(e.target.value);
    setIsEmailDirty(false);
  };

  const handlePasswordOnChange = e => {
    setPassword(e.target.value);
    setIsCredentialsIncorrect(false);
    setIsUserAuthenticationLocked(false);
    setIsPasswordDirty(false);
  };

  const handlePasswordOnBlur = () => {
    if (password) {
      setIsPasswordDirty(true);
    }
  };

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();

    const isEmailValid = isEmailAtValid(email) && isLengthValid(email);
    const isPasswordValid = !!password;
    const isValid = isEmailValid && isPasswordValid && !isCredentialsIncorrect;

    const userSignIn = (captchaToken: string) => {
      storePlatform === StorePlatform.EMERSON
        ? signInEmersonUser(signInMutationVariables(captchaToken))
        : signInUser(signInMutationVariables(captchaToken));
    };

    if (isValid && captchaBypassToken) {
      userSignIn(captchaBypassToken);
    } else if (isValid) {
      gRecaptchaExecute(captchaToken => {
        userSignIn(captchaToken);
      }, setRecaptchaError);
    }
    !isLengthValid(email) ? setIsEmailDirty(true) : setIsPasswordDirty(true);
  };

  const renderForgotPasswordLink = () => {
    const fullLink = `${forgotPasswordLink}?email=${encodeURIComponent(email)}` as const;

    if (isOAuth) {
      history.pushState({ target: "signin" }, "", window.location.href);

      return (
        <Link href={fullLink} isColor="system">
          {t(l.signIn.ForgotPassword)}
        </Link>
      );
    } else {
      return (
        <Link to={fullLink} isColor="system">
          {t(l.signIn.ForgotPassword)}
        </Link>
      );
    }
  };

  const renderCheckbox = () => {
    if (storePlatform === StorePlatform.FULLSCRIPT) {
      return (
        <div css={styles.checkboxWrapper}>
          <Checkbox checked={rememberMe} onChange={() => setRememberMe(!rememberMe)}>
            <Typography>{t(l.signIn.StaySignedIn)}</Typography>
          </Checkbox>
        </div>
      );
    }
  };

  const renderSignInButton = (isFullWidth = true) => (
    <Button
      isLoading={storePlatform === StorePlatform.EMERSON ? isEmersonSignInLoading : isSignInLoading}
      isFullWidth={isFullWidth}
      type="submit"
      data-e2e={e2eAttributes.login.signInButton}
    >
      {t(l.signIn.SignIn)}
    </Button>
  );

  const renderBottomActions = () => {
    if (onBackButtonClick) {
      return (
        <>
          <Spacer height="one" />
          <Arrange isVertical spacing="large" isFullWidth>
            <Typography css={styles.textLeft}>{renderForgotPasswordLink()}</Typography>
            <Arrange justify="spaceBetween">
              <Button
                onClick={onBackButtonClick}
                intention="secondary"
                disabled={
                  storePlatform === StorePlatform.EMERSON ? isEmersonSignInLoading : isSignInLoading
                }
              >
                {t(l.sharedCommon.Back)}
              </Button>
              {renderSignInButton(false)}
            </Arrange>
          </Arrange>
        </>
      );
    }

    return (
      <Columns customCss={styles.row}>
        <Columns.Column columnWidth={6}>
          <Typography css={styles.textLeft}>{renderForgotPasswordLink()}</Typography>
        </Columns.Column>
        <Columns.Column columnWidth={6}>{renderSignInButton()}</Columns.Column>
      </Columns>
    );
  };

  return (
    <>
      {!!authError && <UnauthorizedErrorBox />}
      {!!recaptchaError && <RecaptchaErrorBox />}
      {!!isUserAuthenticationLocked && <AuthenticationLockedMessage />}
      <form noValidate css={styles.form} onSubmit={handleSubmit}>
        <SignInEmailInput
          required
          value={email}
          onChange={handleEmailOnChange}
          isDirty={isEmailDirty}
          onBlur={handleEmailOnBlur}
          descriptiveText={getPasswordPrompt()}
          autoFocus={isEmailAutoFocus}
          wrapperStyles={styles.marginBottom}
        />
        <SignInPasswordInput
          isCredentialsIncorrect={isCredentialsIncorrect}
          required
          onChange={handlePasswordOnChange}
          value={password}
          isDirty={isPasswordDirty}
          onBlur={handlePasswordOnBlur}
          isLoading={false}
          autoFocus={isPasswordAutoFocus}
          wrapperStyles={styles.marginBottom}
          data-e2e={e2eAttributes.login.signInPassword}
        />
        {renderCheckbox()}
        {renderBottomActions()}
      </form>
    </>
  );
};

export { SignInForm };
